import { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Alert, Box, Snackbar, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLayout } from "../../contexts/LayoutContext";
import { ADDRESSBOOK_COLLECTION_KEYS } from "../../graphQL/addressbook";
import { useAddressbookCollection } from "./useAddressbookCollection";
import { toContactCard } from "./presenters";
import { apiCreateContact, apiUpdateContact, apiDeleteContact, apiGetContactById, apiGetContactsCollection } from "../../graphQL/addressbook/collections/contacts";
import { useContactCategoryOptions } from "../../components/component-contact/useContactCategories";
import ResizablePanel from "../../components/common/ResizablePanel";
import ComposeWithMagicDialog from "../../components/spark/ComposeWithMagicDialog";
import ComposeTextDialog from "../../components/spark/ComposeTextDialog";
import { ContactListPanel } from "../../components/component-contact";
import ContactDetailPanel from "./panels/ContactDetailPanel";
import SendEmailToCategoryDialog from "./panels/SendEmailToCategoryDialog";
import ImportContactsDialog from "./panels/ImportContactsDialog";
import JournalPanel from "./panels/JournalPanel";
import CommentsPanel from "./panels/CommentsPanel";
import ChatPanel from "./panels/ChatPanel";
import RemindersPanel from "./panels/RemindersPanel";
import FilesPanel from "./panels/FilesPanel";
import ContactPropertiesPanel from "./panels/ContactPropertiesPanel";
import PropertyDetailPanel from "../properties/panels/PropertyDetailPanel";
import { newPropertyDraft } from "../properties/presenters";
import { apiCreatePropertyAsset, apiUpdatePropertyAsset, apiGetPropertyAsset } from "../../graphQL/properties/collections/propertyAssets";
import { useUploadContext } from "../../Provider/UploadContext";
import { SELECT_ENTITY_JOURNAL_EVENT } from "../../utils/constants";

const COLLECTION_KEY = ADDRESSBOOK_COLLECTION_KEYS.CONTACTS;
const ADDRESSBOOK_SELECTED_CONTACT_KEY = "contextstory.addressbook.selectedContactId";

const PageAddressbook = () => {
  const authorId = useSelector((state) => state.auth.userId);
  const user = useSelector((state) => state.auth.user);
  const role = String(user?.role || "").toLowerCase();
  const isSuperAdmin =
    user?.isSuperAdmin === true ||
    role === "superadmin" ||
    role === "super_admin" ||
    role === "sysadmin" ||
    role.includes("super");
  const userDomain = String(user?.domain || "").trim();
  const { isPanelOpen, togglePanel, setPanels, setAddressbookToolbar, resetAddressbookToolbar } = useLayout();
  const { uploadSingle } = useUploadContext() || {};
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [domainFilter, setDomainFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [composeContact, setComposeContact] = useState(null);
  const [composeTextContact, setComposeTextContact] = useState(null);
  const [addContactToast, setAddContactToast] = useState({ open: false, message: "", severity: "error" });
  // Property selected (or being created) from the linked-properties panel.
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedPropertyFull, setSelectedPropertyFull] = useState(null);
  const [linkedPropertiesRefresh, setLinkedPropertiesRefresh] = useState(0);
  const rowsPerPage = 25;
  const pagination = useMemo(() => ({ page, limit: rowsPerPage }), [page]);
  const ordering = useMemo(() => ({ field: "updatedAt", order: "Desc" }), []);
  const selectedSuperAdminDomain =
    isSuperAdmin && userDomain && userDomain !== "All" ? userDomain : "";
  const activeDomain = isSuperAdmin
    ? (selectedSuperAdminDomain || domainFilter)
    : userDomain;
  const filters = useMemo(
    () => {
      const f = {};
      if (searchFilter) f.text = searchFilter;
      if (activeDomain) f.domain = activeDomain;
      if (categoryFilter) f.category = categoryFilter;
      return f;
    },
    [searchFilter, activeDomain, categoryFilter]
  );
  const activeFilters = useMemo(() => {
    const list = [];
    if (activeDomain) list.push(`domain=${activeDomain}`);
    if (categoryFilter) list.push(`contact type=${categoryFilter}`);
    if (searchFilter) list.push(`text="${searchFilter}"`);
    return list;
  }, [activeDomain, categoryFilter, searchFilter]);
  const selectedContactStorageKey = useMemo(
    () => `${ADDRESSBOOK_SELECTED_CONTACT_KEY}:${activeDomain || "all"}`,
    [activeDomain]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const savedId = window.localStorage.getItem(selectedContactStorageKey);
      if (!savedId) return;
      setSelectedContact((prev) => prev || { id: savedId });
    } catch {
      // no-op
    }
  }, [selectedContactStorageKey]);

  // A reminder row asked to open a contact's journal while this page is
  // already mounted (localStorage restore only runs on mount).
  useEffect(() => {
    const handler = (e) => {
      const { collection, id } = e.detail || {};
      if (collection !== "contacts" || !id) return;
      setSelectedContact({ id });
    };
    window.addEventListener(SELECT_ENTITY_JOURNAL_EVENT, handler);
    return () => window.removeEventListener(SELECT_ENTITY_JOURNAL_EVENT, handler);
  }, []);

  const { loading, error, total, items, reload, patchItem, removeItem } = useAddressbookCollection({
    authorId: null,
    collectionKey: COLLECTION_KEY,
    pagination,
    ordering,
    filters,
    allowWithoutAuthorId: true,
  });

  const [visibleItems, setVisibleItems] = useState([]);
  useEffect(() => {
    setVisibleItems((prev) => {
      if (page <= 1) return items;
      if (!Array.isArray(items) || items.length === 0) return prev;
      const seen = new Set(prev.map((it) => it?.id).filter(Boolean));
      const appended = items.filter((it) => {
        const id = it?.id;
        if (!id) return true;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      return appended.length ? [...prev, ...appended] : prev;
    });
  }, [items, page]);

  const contacts = useMemo(() => visibleItems.map(toContactCard), [visibleItems]);
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));

  const settingsAuthorId = user?.id || user?.userId || authorId;
  const categoryOptions = useContactCategoryOptions(settingsAuthorId);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // Full document (incl. the `data` bag) for the selected contact. List items
  // are slim, so detail-side panels hydrate from a single-doc fetch.
  const [selectedContactFull, setSelectedContactFull] = useState(null);
  useEffect(() => {
    const id = selectedContact?.id;
    if (!id || String(id).startsWith("draft-")) {
      setSelectedContactFull(null);
      return undefined;
    }
    let cancelled = false;
    // Domain-scoped list lookup (full doc): the single-contact query is
    // author-scoped on the backend and errors on other users' contacts.
    apiGetContactsCollection({
      full: true,
      filters: { ids: [id], ...(activeDomain ? { domain: activeDomain } : {}) },
      pagination: { page: 1, limit: 1 },
    })
      .then((res) => {
        const doc = res?.items?.[0];
        if (!cancelled && doc?.id === id) setSelectedContactFull(doc);
      })
      .catch((err) => console.error("Failed to load contact details:", err));
    return () => { cancelled = true; };
  }, [selectedContact?.id, authorId, activeDomain]);

  const selectedRawContact = useMemo(() => {
    if (!selectedContact) return null;
    const fromList = visibleItems.find((c) => c.id === selectedContact.id) || null;
    const full = selectedContactFull?.id === selectedContact.id ? selectedContactFull : null;
    // List item fields win (they carry local optimistic patches); the full doc
    // contributes the heavyweight fields (`data`, `files`) the list omits.
    if (fromList && full) return { ...full, ...fromList };
    if (full || fromList) return full || fromList;
    // Newly-created contact may not be in the current `items` page yet (reload pending).
    // Fall back to the object we cached on setSelectedContact so the detail panel renders.
    return selectedContact.identity || selectedContact.reach ? selectedContact : null;
  }, [selectedContact, visibleItems, selectedContactFull]);

  useEffect(() => {
    if (!selectedContact?.id) return;
    const hydrated = contacts.find((c) => c.id === selectedContact.id);
    if (hydrated) setSelectedContact(hydrated);
  }, [contacts, selectedContact?.id]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (selectedContact?.id) {
        window.localStorage.setItem(selectedContactStorageKey, selectedContact.id);
      } else {
        window.localStorage.removeItem(selectedContactStorageKey);
      }
    } catch {
      // no-op
    }
  }, [selectedContact?.id, selectedContactStorageKey]);

  const handlePreviousPage = () => {
    setPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setPage((prev) => (prev < totalPages ? prev + 1 : prev));
  };

  const handleSearchSubmit = useCallback(() => {
    setVisibleItems([]);
    setSearchFilter(searchInput.trim());
    setPage(1);
  }, [searchInput]);

  const handleSearchChange = useCallback((v) => {
    setSearchInput(v);
    if (!v.trim()) {
      setVisibleItems([]);
      setSearchFilter("");
      setPage(1);
    }
  }, []);

  useEffect(() => {
    setAddressbookToolbar({
      searchQuery: searchInput || "",
      onSearchChange: handleSearchChange,
      onSearchSubmit: handleSearchSubmit,
    });
    return () => {
      resetAddressbookToolbar();
    };
  }, [
    searchInput,
    handleSearchChange,
    handleSearchSubmit,
    setAddressbookToolbar,
    resetAddressbookToolbar,
  ]);

  const handleSaveContact = useCallback(async (changes) => {
    if (!selectedRawContact) return;
    try {
      const saved = await apiUpdateContact({
        id: selectedRawContact.id,
        ...changes,
      });
      if (saved?.id) {
        // Patch the saved doc in place instead of refetching the whole page.
        patchItem(saved.id, saved);
        setVisibleItems((prev) => prev.map((it) => (it?.id === saved.id ? { ...it, ...saved } : it)));
        setSelectedContactFull(saved);
      } else {
        reload();
      }
    } catch (err) {
      console.error("Failed to save contact:", err);
    }
  }, [selectedRawContact, patchItem, reload]);

  const handleDeleteContact = useCallback(async (contactId) => {
    await apiDeleteContact({ id: contactId, authorId });
    setSelectedContact(null);
    removeItem(contactId);
    setVisibleItems((prev) => prev.filter((it) => it?.id !== contactId));
  }, [authorId, removeItem]);

  const handleStatusChange = useCallback(async (contact, status) => {
    if (!contact?.id) return;
    patchItem(contact.id, { status });
    setVisibleItems((prev) => prev.map((it) => (it?.id === contact.id ? { ...it, status } : it)));
    try {
      await apiUpdateContact({ id: contact.id, status });
    } catch (error) {
      console.error("Failed to update contact status:", error);
    }
  }, [patchItem]);

  const handleCategoryChange = useCallback(async (contact, category) => {
    if (!contact?.id) return;
    patchItem(contact.id, { category });
    setVisibleItems((prev) => prev.map((it) => (it?.id === contact.id ? { ...it, category } : it)));
    try {
      await apiUpdateContact({ id: contact.id, category });
    } catch (error) {
      console.error("Failed to update contact category:", error);
    }
  }, [patchItem]);

  const showAddContactToast = useCallback((message, severity = "error") => {
    setAddContactToast({ open: true, message, severity });
  }, []);

  const handleAddContact = useCallback(async () => {
    console.log("[+] handleAddContact fired", { authorId, activeDomain });
    const idToken = window.localStorage.getItem("authToken");
    if (!authorId) {
      console.error("[+] Cannot create contact: authorId missing — are you logged in?");
      showAddContactToast(
        [
          "Could not add contact. Possible reasons:",
          "- Missing authorId in session (Redux auth state not hydrated).",
          "- Missing/invalid auth token in localStorage.",
          "- Login token missing user id claims (sub/cognito:username/email).",
          "- Session expired or login redirect did not complete.",
          "- Browser storage was cleared or blocked.",
          idToken ? "- authToken exists but userId could not be parsed." : "- authToken key is missing.",
        ].join("\n")
      );
      return;
    }
    try {
      const newContact = await apiCreateContact({
        authorId,
        ...(activeDomain ? { domain: activeDomain } : {}),
        identity: { person: { firstName: "", lastName: "" }, company: {} },
        reach: { emails: [], phones: [], social: [], addresses: [] },
      });
      console.log("[+] apiCreateContact returned", newContact);
      if (!newContact) {
        console.error("[+] apiCreateContact returned null/undefined");
        showAddContactToast(
          [
            "Could not add contact. Possible reasons:",
            "- GraphQL createContact returned an empty response.",
            "- Backend rejected input (validation/authorization).",
            "- API schema mismatch between frontend and backend.",
            "- Network/auth issue during mutation.",
          ].join("\n")
        );
        return;
      }
      setPage(1);
      setSelectedContact(newContact);
      setPanels({ details: true });
      reload();
    } catch (e) {
      console.error("[+] apiCreateContact threw:", e);
      showAddContactToast(
        [
          "Could not add contact. Possible reasons:",
          `- ${e?.message || "Unknown runtime error"}`,
          "- Network/API unavailable.",
          "- Authorization failure for createContact.",
          "- Backend validation failure.",
          "- Expired auth/session token.",
        ].join("\n")
      );
    }
  }, [authorId, activeDomain, reload, setPanels, showAddContactToast]);

  // ── Linked properties: select / add / save ──────────────────────────────
  useEffect(() => {
    // Changing contact drops any property selection or unsaved draft.
    setSelectedProperty(null);
    setSelectedPropertyFull(null);
  }, [selectedContact?.id]);

  useEffect(() => {
    const id = selectedProperty?.id;
    if (!id || selectedProperty?._isNew || String(id).startsWith("draft-")) {
      setSelectedPropertyFull(null);
      return undefined;
    }
    let cancelled = false;
    apiGetPropertyAsset(id)
      .then((doc) => { if (!cancelled && doc?.id === id) setSelectedPropertyFull(doc); })
      .catch((err) => console.error("Failed to load property details:", err));
    return () => { cancelled = true; };
  }, [selectedProperty?.id, selectedProperty?._isNew]);

  const selectedPropertyRaw = useMemo(() => {
    if (!selectedProperty) return null;
    if (selectedProperty._isNew) return selectedProperty;
    return selectedPropertyFull?.id === selectedProperty.id
      ? { ...selectedPropertyFull, ...selectedProperty }
      : selectedProperty;
  }, [selectedProperty, selectedPropertyFull]);

  const handleSelectProperty = useCallback((property) => {
    if (!property?.id) return;
    setSelectedProperty(property);
    setPanels({ propertyDetail: true });
  }, [setPanels]);

  const handleAddProperty = useCallback(() => {
    if (!selectedRawContact?.id) return;
    setSelectedProperty(newPropertyDraft());
    setPanels({ propertyDetail: true });
  }, [selectedRawContact?.id, setPanels]);

  // Mirror the backend's propertyListIds sync into local contact state so the
  // linked list and the card refresh without a refetch.
  const linkPropertyToContactLocally = useCallback((contactId, propertyId) => {
    const addId = (c) => {
      const prev = Array.isArray(c?.propertyListIds) ? c.propertyListIds : [];
      return prev.includes(propertyId) ? c : { ...c, propertyListIds: [...prev, propertyId] };
    };
    patchItem(contactId, addId(visibleItems.find((it) => it?.id === contactId) || {}));
    setVisibleItems((prev) => prev.map((it) => (it?.id === contactId ? addId(it) : it)));
    setSelectedContactFull((prev) => (prev?.id === contactId ? addId(prev) : prev));
    setLinkedPropertiesRefresh((n) => n + 1);
  }, [patchItem, visibleItems]);

  const handleSaveProperty = useCallback(async (updated) => {
    if (!updated) return;
    const contactId = selectedRawContact?.id;
    if (updated._isNew) {
      if (!contactId) return;
      try {
        const { name, status, category, identity, details } = updated;
        const created = await apiCreatePropertyAsset({
          authorId,
          domain: activeDomain || userDomain || "",
          name: name || "New Property",
          ...(status ? { status } : {}),
          ...(category ? { category } : {}),
          ...(identity ? { identity } : {}),
          ...(details ? { details } : {}),
          contactList: [{ contactId, role: "" }],
        });
        if (created?.id) {
          setSelectedProperty(created);
          setSelectedPropertyFull(created);
          linkPropertyToContactLocally(contactId, created.id);
        }
      } catch (err) {
        console.error("Failed to create property:", err);
      }
      return;
    }
    if (!updated.id) return;
    try {
      const { id, name, status, category, identity, details } = updated;
      const result = await apiUpdatePropertyAsset({ id, name, status, category, identity, details });
      if (result?.id) {
        setSelectedProperty(result);
        setSelectedPropertyFull(result);
        setLinkedPropertiesRefresh((n) => n + 1);
      }
    } catch (err) {
      console.error("Failed to save property:", err);
    }
  }, [selectedRawContact?.id, authorId, activeDomain, userDomain, linkPropertyToContactLocally]);

  const closePropertyDetail = useCallback(() => {
    setPanels({ propertyDetail: false });
    setSelectedProperty((prev) => (prev?._isNew ? null : prev));
  }, [setPanels]);

  const contactsOpen = isPanelOpen("contacts");
  const detailsOpen = isPanelOpen("details");
  const commentsOpen = isPanelOpen("comments");
  const chatOpen = isPanelOpen("chat");
  const journalOpen = isPanelOpen("journal");
  const remindersOpen = isPanelOpen("reminders");
  const filesOpen = isPanelOpen("files");
  const propertiesOpen = isPanelOpen("properties");
  const propertyDetailOpen = isPanelOpen("propertyDetail") && Boolean(selectedPropertyRaw);

  const lastPanel = propertyDetailOpen ? "propertyDetail" : propertiesOpen ? "properties" : filesOpen ? "files" : remindersOpen ? "reminders" : journalOpen ? "journal" : chatOpen ? "chat" : commentsOpen ? "comments" : detailsOpen ? "details" : contactsOpen ? "contacts" : null;

  // Files panel state (admin-style controlled inputs)
  const [filesDisplayName, setFilesDisplayName] = useState("");
  const [filesSelectedType, setFilesSelectedType] = useState("");
  const [filesSelectedFile, setFilesSelectedFile] = useState(null);
  const [filesStatus, setFilesStatus] = useState("");
  const [filesProgress, setFilesProgress] = useState(0);

  const handleContactFileSave = useCallback(async () => {
    // Files are saved via apiCreateFile/apiUpdateFile inside FilesPanel itself
    // and live in the `files` collection; the contact list renders nothing
    // derived from them, so no list refetch is needed.
  }, []);

  const [remindersRefreshKey, setRemindersRefreshKey] = useState(0);

  const openComments = () => setPanels({ comments: true, details: false, chat: false });
  const closeComments = () => setPanels({ comments: false, details: true });
  const openChat = () => {
    if (isMobile) {
      setAddContactToast({ open: true, message: "Use your desktop for full access.", severity: "info" });
      return;
    }
    setPanels({ chat: true, details: false, comments: false });
  };
  const closeChat = () => setPanels({ chat: false, details: true });

  const handleChatAction = useCallback((toolName, updatedReminder) => {
    if (toolName === "create_reminder" || toolName === "create_global_reminder" || toolName === "update_reminder") {
      setRemindersRefreshKey((k) => k + 1);
      if (updatedReminder) setSelectedReminder(updatedReminder);
    }
    if (toolName === "update_contact") {
      // Chat mutated the contact server-side; refetch just that doc and patch
      // it in place instead of reloading the whole page.
      const id = selectedContact?.id;
      if (id) {
        apiGetContactById({ id, authorId })
          .then((doc) => {
            if (!doc?.id) return;
            patchItem(doc.id, doc);
            setVisibleItems((prev) => prev.map((it) => (it?.id === doc.id ? { ...it, ...doc } : it)));
            setSelectedContactFull(doc);
          })
          .catch((err) => console.error("Failed to refresh contact after chat update:", err));
      } else {
        reload();
      }
    }
  }, [reload, selectedContact?.id, authorId, patchItem]);

  return (
    <Box sx={{
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      height: "100%",
      minHeight: 0,
      minWidth: 0,
      overflow: "hidden",
      gap: isMobile ? 0.75 : 1,
    }}>
      {contactsOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 360}
          minWidth={isMobile ? window.innerWidth : 260}
          maxWidth={isMobile ? window.innerWidth : 520}
          fillRemaining={!isMobile && lastPanel === "contacts"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <ContactListPanel
            loading={loading && page <= 1}
            loadingMore={loading && page > 1}
            error={error}
            contacts={contacts}
            total={total}
            page={page}
            totalPages={totalPages}
            canPrevious={page > 1}
            canNext={page < totalPages}
            onPrevious={handlePreviousPage}
            onNext={handleNextPage}
            searchQuery={searchInput}
            onSearchChange={handleSearchChange}
            onSearchSubmit={handleSearchSubmit}
            onClose={() => togglePanel("contacts")}
            onAdd={handleAddContact}
            onImport={() => setImportOpen(true)}
            selectedId={selectedContact?.id}
            onSelect={(contact) => setSelectedContact(contact)}
            onCompose={(card) => {
              const raw = visibleItems.find((c) => c.id === card.id);
              if (raw) setComposeContact(raw);
            }}
            onComposeText={(card) => {
              const raw = visibleItems.find((c) => c.id === card.id);
              if (raw) setComposeTextContact(raw);
            }}
            activeFilters={activeFilters}
            categoryFilter={categoryFilter}
            onCategoryFilterChange={(value) => {
              setVisibleItems([]);
              setCategoryFilter(value || "");
              setPage(1);
            }}
            onStatusChange={handleStatusChange}
            onCategoryChange={handleCategoryChange}
            categoryOptions={categoryOptions}
            onBulkEmail={() => setBulkEmailOpen(true)}
            onDeleteContact={(contact) => handleDeleteContact(contact?.id)}
            onLoadMore={handleNextPage}
            hasMore={page < totalPages}
          />
        </ResizablePanel>
      )}

      {detailsOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 700}
          minWidth={isMobile ? window.innerWidth : 300}
          maxWidth={isMobile ? window.innerWidth : 700}
          fillRemaining={!isMobile && lastPanel === "details"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <ContactDetailPanel
            contact={selectedRawContact}
            onClose={() => togglePanel("details")}
            onSave={handleSaveContact}
            onDelete={handleDeleteContact}
            onOpenComments={openComments}
            onOpenChat={openChat}
          />
        </ResizablePanel>
      )}

      {commentsOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 700}
          minWidth={isMobile ? window.innerWidth : 300}
          maxWidth={isMobile ? window.innerWidth : 700}
          fillRemaining={!isMobile && lastPanel === "comments"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <CommentsPanel
            contact={selectedRawContact}
            authorId={authorId}
            onClose={closeComments}
          />
        </ResizablePanel>
      )}

      {chatOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 700}
          minWidth={isMobile ? window.innerWidth : 300}
          maxWidth={isMobile ? window.innerWidth : 700}
          fillRemaining={!isMobile && lastPanel === "chat"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <ChatPanel
            contact={selectedRawContact}
            authorId={authorId}
            onClose={closeChat}
            onActionComplete={handleChatAction}
            selectedReminder={selectedReminder}
          />
        </ResizablePanel>
      )}

      {propertiesOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 460}
          minWidth={isMobile ? window.innerWidth : 320}
          maxWidth={isMobile ? window.innerWidth : 700}
          fillRemaining={!isMobile && lastPanel === "properties"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <ContactPropertiesPanel
            contact={selectedRawContact}
            onClose={() => togglePanel("properties")}
            selectedId={selectedProperty?.id}
            onSelect={handleSelectProperty}
            onOpenDetails={handleSelectProperty}
            onAdd={handleAddProperty}
            refreshKey={linkedPropertiesRefresh}
          />
        </ResizablePanel>
      )}

      {propertyDetailOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 520}
          minWidth={isMobile ? window.innerWidth : 360}
          maxWidth={isMobile ? window.innerWidth : 900}
          fillRemaining={!isMobile && lastPanel === "propertyDetail"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <PropertyDetailPanel
            property={selectedPropertyRaw}
            onSave={handleSaveProperty}
            onClose={closePropertyDetail}
          />
        </ResizablePanel>
      )}

      {journalOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 380}
          minWidth={isMobile ? window.innerWidth : 280}
          maxWidth={isMobile ? window.innerWidth : 500}
          fillRemaining={!isMobile && lastPanel === "journal"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <JournalPanel
            contact={selectedRawContact}
            authorId={authorId}
            onClose={() => togglePanel("journal")}
            refreshKey={remindersRefreshKey}
            selectedReminderId={selectedReminder?.id}
            onSelectReminder={(r) => {
              const deselecting = selectedReminder?.id === r.id;
              setSelectedReminder(deselecting ? null : r);
            }}
          />
        </ResizablePanel>
      )}

      {remindersOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 380}
          minWidth={isMobile ? window.innerWidth : 280}
          maxWidth={isMobile ? window.innerWidth : 500}
          fillRemaining={!isMobile && lastPanel === "reminders"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <RemindersPanel
            authorId={authorId}
            onClose={() => togglePanel("reminders")}
            refreshKey={remindersRefreshKey}
            selectedReminderId={selectedReminder?.id}
            onSelectReminder={(r) => {
              const deselecting = selectedReminder?.id === r.id;
              setSelectedReminder(deselecting ? null : r);
              if (!deselecting) {
                // Tie the reminder back to its contact and the comment section:
                // select the contact (fetching it if the current list page
                // doesn't contain it) and surface the comments panel.
                const rData = typeof r.data === "string" ? JSON.parse(r.data) : (r.data || {});
                const cId = (r.ref?.collection === "contacts" && r.ref?.id) || rData.contactId;
                if (cId) {
                  const raw = items.find((c) => c.id === cId);
                  if (raw) {
                    setSelectedContact(toContactCard(raw));
                  } else {
                    // Domain-scoped list lookup: the single-contact query is
                    // author-scoped and misses other users' contacts.
                    apiGetContactsCollection({
                      filters: { ids: [cId], ...(activeDomain ? { domain: activeDomain } : {}) },
                      pagination: { page: 1, limit: 1 },
                    })
                      .then((res) => {
                        const c = res?.items?.[0];
                        if (c?.id) setSelectedContact(toContactCard(c));
                      })
                      .catch(() => {});
                  }
                  setPanels({ comments: true, details: false, chat: false });
                }
              }
            }}
          />
        </ResizablePanel>
      )}

      {filesOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 520}
          minWidth={isMobile ? window.innerWidth : 320}
          maxWidth={isMobile ? window.innerWidth : 900}
          fillRemaining={!isMobile && lastPanel === "files"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <FilesPanel
            contactInfo={selectedRawContact || { id: "", details: { files: [] } }}
            updateContactInfo={() => {}}
            uploadSingle={uploadSingle}
            actionSave={handleContactFileSave}
            filesDisplayName={filesDisplayName}
            setFilesDisplayName={setFilesDisplayName}
            filesSelectedType={filesSelectedType}
            setFilesSelectedType={setFilesSelectedType}
            filesSelectedFile={filesSelectedFile}
            setFilesSelectedFile={setFilesSelectedFile}
            filesStatus={filesStatus}
            setFilesStatus={setFilesStatus}
            filesProgress={filesProgress}
            setFilesProgress={setFilesProgress}
            onClose={() => togglePanel("files")}
            fileSettingsType="contact"
            fileRefCollection="contacts"
            fileRefSchemaType="contact"
            fileRefName="contacts"
            emptySelectionMessage="Select contact first"
            previewEntityType="contact"
            entityName={(() => {
              const idn = selectedRawContact?.identity || {};
              const person = idn.person || {};
              const company = idn.company || {};
              const full = `${person.firstName || ""} ${person.lastName || ""}`.trim();
              return full || company.name || idn.companyName || "";
            })()}
          />
        </ResizablePanel>
      )}

      <ComposeWithMagicDialog
        open={Boolean(composeContact)}
        onClose={() => setComposeContact(null)}
        contact={composeContact}
      />
      <ComposeTextDialog
        open={Boolean(composeTextContact)}
        onClose={() => setComposeTextContact(null)}
        contact={composeTextContact}
      />
      <ImportContactsDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        domain={activeDomain}
        authorId={settingsAuthorId}
        onComplete={() => {
          setVisibleItems([]);
          setPage(1);
          reload();
        }}
      />
      <SendEmailToCategoryDialog
        open={bulkEmailOpen}
        onClose={() => setBulkEmailOpen(false)}
        categoryOptions={categoryOptions}
        defaultCategory={categoryFilter}
        authorId={authorId}
        domain={activeDomain}
        defaultFrom={user?.email || ""}
      />
      <Snackbar
        open={addContactToast.open}
        autoHideDuration={9000}
        onClose={() => setAddContactToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={addContactToast.severity}
          variant="filled"
          onClose={() => setAddContactToast((prev) => ({ ...prev, open: false }))}
          sx={{ whiteSpace: "pre-line", maxWidth: 720, alignItems: "flex-start" }}
        >
          {addContactToast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PageAddressbook;
